import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function CombinatoricsPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        W zadaniach maturalnych z kombinatoryki najważniejsze jest rozpoznanie,
        <strong> co wybieramy</strong>, czy <strong>kolejność ma znaczenie</strong>
        oraz czy elementy mogą się powtarzać. Dopiero potem wybieramy sposób liczenia.
      </p>
      <p>
        <strong>Zasada mnożenia:</strong> jeśli wybór składa się z kolejnych etapów,
        liczbę możliwości na etapach mnożymy. Gdy pierwszy etap ma
        <InlineMath math="a" /> możliwości, a drugi <InlineMath math="b" /> możliwości,
        łącznie mamy:
      </p>
      <BlockMath math={String.raw`a\cdot b`} />
      <p>
        <strong>Zasada dodawania:</strong> jeśli dzielimy rozwiązania na rozłączne
        przypadki — na przykład wybieramy zestaw mięsny <em>albo</em> wegetariański —
        liczby możliwości w przypadkach dodajemy.
      </p>
      <BlockMath math={String.raw`N=N_1+N_2+\ldots+N_k`} />
      <p>
        Gdy ustawiamy wszystkie <InlineMath math="n" /> różnych elementów w kolejności,
        stosujemy <strong>permutację</strong>:
      </p>
      <BlockMath math={String.raw`P_n=n!=1\cdot2\cdot\ldots\cdot n`} />
      <p>
        Gdy obsadzamy tylko <InlineMath math="k" /> uporządkowanych miejsc,
        mamy <strong>wariację</strong>. Bez powtórzeń otrzymujemy
        <InlineMath math={String.raw`\frac{n!}{(n-k)!}`} />, a z powtórzeniami
        <InlineMath math={String.raw`n^k`} />. Typowe zadania o kodach i liczbach
        wielocyfrowych są właśnie wariacjami, często z dodatkowym warunkiem na pierwszą
        lub ostatnią cyfrę.
      </p>
      <p>
        Gdy wybieramy grupę i kolejność nie ma znaczenia, stosujemy
        <strong> kombinację</strong>:
      </p>
      <BlockMath math={String.raw`{n\choose k}=\frac{n!}{k!(n-k)!}`} />
      <p>
        Praktyczna kolejność pracy: rozpisz miejsca lub przypadki, uwzględnij warunek
        specjalny, policz możliwości w każdym przypadku i dopiero na końcu dodaj wyniki.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Zasada mnożenia i dodawania",
      description: "Zestaw A: 3 zupy i 4 dania. Zestaw B: 2 zupy i 5 dań. Wybieramy jeden zestaw.",
      content: <BlockMath math={String.raw`3\cdot4+2\cdot5=12+10=22`} />,
    },
    {
      label: "Przykład 2",
      summary: "Liczby trzycyfrowe nieparzyste",
      description: "Tworzymy liczby z cyfr 1, 2, 3, 4, 5 bez powtórzeń.",
      content: (
        <div>
          <p>Najpierw wybieramy cyfrę jedności: 3 możliwości (1, 3 lub 5).</p>
          <BlockMath math={String.raw`3\cdot4\cdot3=36`} />
          <p>Po wyborze jedności na setki zostają 4 cyfry, a na dziesiątki 3.</p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Permutacja",
      description: "Pięć różnych osób ustawia się w kolejce.",
      content: <BlockMath math={String.raw`5!=5\cdot4\cdot3\cdot2\cdot1=120`} />,
    },
    {
      label: "Przykład 4",
      summary: "Kombinacja",
      description: "Z ośmiu osób wybieramy trzyosobową drużynę. Kolejność wyboru nie ma znaczenia.",
      content: <BlockMath math={String.raw`{8\choose3}=\frac{8!}{3!5!}=56`} />,
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
